import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import type { TranslationKey } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import type { BLOCKED_REASON, BlockedReason } from '../types';

/**
 * What the three refusals need from their caller — and the type is where the one rule that splits them lives.
 *
 * **Only `consent` has a way back** [decisions 0160, 0314]: a decline is a choice the candidate can remake,
 * and the explainer hands them back to the modal. **`age` and `auth` END the application** [decision 0161,
 * ISS — Heidi Carrel: *"the fact that they can't proceed past that … must haves"*; ISS-201]. A way back from
 * either reopens the form with the answer editable, and the candidate flips one toggle and is created in
 * Bullhorn — which is what shipped until ISS-201.
 *
 * So a terminal refusal does not merely *omit* `onGoBack`: it cannot be handed one. A caller that tries is
 * a typecheck failure, not a review finding.
 */
export type BlockedScreenProps = ConsentRefusalProps | EligibilityRefusalProps;

interface CommonRefusalProps {
  /** Ends the candidate's session. Nothing was written, so there is nothing to undo [decisions 0195, 0199]. */
  readonly onExit: () => void;
}

interface ConsentRefusalProps extends CommonRefusalProps {
  readonly reason: typeof BLOCKED_REASON.consent;
  /** Back to the consent modal — *"go back and choose 'I agree'"* [0160; 0314]. The header Back does the same. */
  readonly onGoBack: () => void;
}

interface EligibilityRefusalProps extends CommonRefusalProps {
  readonly reason: typeof BLOCKED_REASON.age | typeof BLOCKED_REASON.auth;
  /**
   * What the header Back does here: it ASKS to leave — the Exit question, the same overlay the profile
   * form's own Back opens [ISS-149] — because leaving is the only way out of an ended application [0161].
   * NO keeps the candidate on this screen; YES ends the session. It never returns to the form.
   */
  readonly onAskToExit: () => void;
  readonly onGoBack?: never;
}

/**
 * The three refusals [decisions 0160, 0161, 0195; Figma `STATES & OVERLAYS` → `Blocked — cannot continue` →
 * `Blocked · consent` / `Blocked · age` / `Blocked · auth`, Desktop / iPad / Mobile].
 *
 * One component for three screens, because the frames are the same screen three times over: identical
 * structure, identical actions, and only the heading, the sentence under it and the callout differ. Three
 * files would be three places for the next copy change to miss.
 *
 * **Nothing has been written to Bullhorn when a candidate sees this** [decision 0195]. The gate fires after
 * the consent modal and the create happens only once all three answers pass, which is how [0160]'s *"a
 * decline must leave no candidate behind"* is satisfied: there is no record to orphan, no lead to
 * reconcile, and nothing for a recruiter to clean up.
 *
 * **Consent is not a dead end; age and work authorisation are an end, by ruling.** [0195] flagged the
 * question for consent and could not answer it — *"whether that state offers a way back to the modal… is NOT
 * ruled here"* — and [0314] answered it: the explainer carries a second control back to the choice. Read live
 * on 2026-09-10, all nine frames draw a footer carrying **`Go back and change my answer`** beside `Exit`,
 * plus the header's Back and the Need help pill.
 *
 * ⛔ **For `Blocked · age` and `Blocked · auth` the frame is overruled on behaviour** [ISS-201]. [0161] is
 * ISS's ruling that a `no` to either *"can't proceed past that"* — the application ENDS on the explainer,
 * with no path onward. A footer control back to the form was exactly that path: the candidate changed one
 * answer and the Bullhorn create went through. So on those two the footer carries `Exit` alone, and the
 * header Back asks to leave rather than returning anywhere. The layout is still the frame's — behaviour is
 * the Ground Truth's, and the frames illustrate it [OPERATING-RULES, *which source wins*]. Not a BR-K7
 * dead end: `Exit`, the Exit question and Need help are all still on the screen.
 *
 * ⚠️ The copy is ISS's, taken from the frames verbatim. The `Action link →` the callout instance carries in
 * all nine frames is NOT built: it is the design system's placeholder label with no destination anywhere in
 * the file, and a link to nowhere on an unattended kiosk is worse than no link — the same call `I9Screen`
 * makes for the same placeholder.
 */
export function BlockedScreen(props: BlockedScreenProps) {
  const { reason, onExit } = props;
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const copy = COPY[reason];
  // `undefined` on the two eligibility refusals, by the shape of their props: there is nowhere to go back to.
  const goBack = props.reason === 'consent' ? props.onGoBack : undefined;
  const back = props.reason === 'consent' ? props.onGoBack : props.onAskToExit;

  return (
    <KioskScreen>
      <KioskCard
        footer={
          /*
            A ROW from `sm` up with the two actions at opposite ends, stacked and left-aligned at 375 — the
            frames' own arrangement, and both buttons keep their natural width at every one of the three
            (307 and 79 at all of them; the mobile band is 343 wide, so they fit side by side and are drawn
            stacked anyway).

            `gap-2`, not the footer's own `gap-3`: every `Actions` box in these nine frames sets 8.

            With `Exit` alone (age / auth, ISS-201) it stays where the frames draw it — at the END of the row
            from `sm` up — rather than sliding to the start, which is where `justify-between` puts one child.
          */
          <div
            className={`flex w-full flex-col items-start gap-2 sm:flex-row sm:items-center ${
              goBack === undefined ? 'sm:justify-end' : 'sm:justify-between'
            }`}
          >
            {goBack !== undefined && (
              <Button onClick={goBack} variant="outline">
                {t('blocked.goBack')}
              </Button>
            )}
            <Button onClick={onExit}>{t('blocked.exit')}</Button>
          </div>
        }
        header={
          <>
            {/*
              On `Blocked · consent`, Back does what the footer's secondary does — back to the modal —
              NOT what Back does on the profile form. The candidate has not chosen to leave, and this
              screen's own callout promises *"Nothing you typed has been lost."* A Back that wiped the
              session would break that promise from the one control most likely to be pressed by somebody
              looking for a way out.

              On `Blocked · age` / `Blocked · auth` there is nothing to go back to [0161; ISS-201], so Back
              asks to leave — the Exit question, as on the form — and never reopens the form.
            */}
            <BackLink onClick={back} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
        rhythm="flush"
      >
        {/*
          `py-8`, paired with the card's `flush` rhythm: these frames put 32px INSIDE the content column
          rather than 24 between the card's slots [measured on 12424:25001 — the Content box runs 32291 to
          32522, its heading opens at 32323 and its callout closes at 32490, so 32 at each end].

          The two go together. On the default rhythm this content would sit 24px lower with 32 of its own
          on top, and the 56 would push the callout and both actions off where the frame draws them.
        */}
        <div className="flex w-full flex-col items-start gap-6 py-8">
          {/*
            CENTRED, both lines — and read off the nodes rather than inferred from the neighbouring form,
            which left-aligns everything. All six text nodes report `textAlignHorizontal: CENTER`
            [12424:25086/25087 consent, 25448/25449 age, 25810/25811 auth]. The callout below them stays
            left, exactly as its own component does everywhere else.
          */}
          <div className="flex w-full flex-col items-start gap-2">
            <h1
              className="kiosk-heading-focus w-full text-center text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {t(copy.heading)}
            </h1>
            {/*
              14/17, which is tighter than the 14/24 subtitle every other screen in this app draws. It is
              what these nine frames set (SF Pro Display 400, 14px, 17 line-height), and the number is
              load-bearing rather than cosmetic: the `Blocked · consent` heading block measures 71 as
              29 + 8 + two lines at 17, and at the app's usual leading the same two lines would run the
              block to 85 and push the callout and both actions 14px down.
            */}
            <p className="w-full text-center text-sm leading-[17px] text-[var(--muted-foreground)]">
              {t(copy.body)}
            </p>
          </div>
          <Callout tone="warning">{t(copy.whatToDo)}</Callout>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}

/**
 * Which three strings each refusal shows.
 *
 * An explicit map rather than a built-up `blocked.${reason}.heading`: a template literal resolves at the
 * type level but not at the dictionary level, so a renamed key would still compile and only fail in front of
 * a candidate. Named here, a missing entry is a typecheck failure.
 */
const COPY: Record<
  BlockedReason,
  {
    readonly heading: TranslationKey;
    readonly body: TranslationKey;
    readonly whatToDo: TranslationKey;
  }
> = {
  consent: {
    heading: 'blocked.consent.heading',
    body: 'blocked.consent.body',
    whatToDo: 'blocked.consent.whatToDo',
  },
  age: {
    heading: 'blocked.age.heading',
    body: 'blocked.age.body',
    whatToDo: 'blocked.age.whatToDo',
  },
  auth: {
    heading: 'blocked.auth.heading',
    body: 'blocked.auth.body',
    whatToDo: 'blocked.auth.whatToDo',
  },
};
